import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import Sidebar from './Sidebar';
import './css/LoggedInPage.css';

const API_BASE = '';

const LoggedInPage = () => {
  const { token, logout } = useAuth();

  const [terravaloper, setTerravaloper] = useState('');
  const [walletDetails, setWalletDetails] = useState(null);
  const [fullDetails, setFullDetails] = useState(null);
  const [fetchError, setFetchError] = useState('');
  const [loading, setLoading] = useState(true);
  const [allValidators, setAllValidators] = useState([]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.data?.terravaloper) {
          setFetchError('Missing terravaloper');
          setLoading(false);
          return;
        }

        setTerravaloper(res.data.terravaloper);
      } catch (err) {
        setFetchError('Error fetching user');
        setLoading(false);
      }
    };

    if (token) fetchUser();

    const handleUnload = () => logout();
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [token, logout]);

  useEffect(() => {
    const fetchWalletAddresses = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/lunc/wallet-addresses?operator=${terravaloper}`);
        setWalletDetails(res.data);
        localStorage.setItem('walletDetails', JSON.stringify(res.data));
      } catch (err) {
        setFetchError('Error fetching wallet addresses');
      }
    };

    const fetchFullValidator = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/lunc/validator-full?operator=${terravaloper}`);
        setFullDetails(res.data.data);
      } catch (err) {
        setFetchError('Error fetching validator details');
      } finally {
        setLoading(false);
      }
    };

    const fetchAllValidators = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/lunc/validators`);
        setAllValidators(res.data);
      } catch (err) {
        console.error('❌ Error fetching all validators:', err.message);
      }
    };

    if (terravaloper) {
      fetchWalletAddresses();
      fetchFullValidator();
      fetchAllValidators();
    }
  }, [terravaloper]);

  return (
    <div className="logged-in-page">
      <Sidebar chains={['Terra Classic']} />
      <div className="logged-in-page-content">
        <h1>Your Validator</h1>

        <h2>Recovered Wallets</h2>
        <p><strong>Operator:</strong> {walletDetails?.operator || '🔄 Loading...'}</p>
        <p><strong>Feeder:</strong> {walletDetails?.feeder || '🔄 Loading...'}</p>
        <p><strong>Owner:</strong> {walletDetails?.owner || '🔄 Loading...'}</p>
        <p><strong>Consensus:</strong> {walletDetails?.consensus || '🔄 Loading...'}</p>
        <p><strong>Hex:</strong> {walletDetails?.hex || '🔄 Loading...'}</p>

        <p><strong>Terravaloper Address:</strong> {terravaloper || '🔄 Loading...'}</p>

        {fetchError && <p style={{ color: 'red' }}>{fetchError}</p>}

        {fullDetails && (
          <div className="validator-sections">
            <h2>Validator Details</h2>
            <DisplaySection title="Staking" data={fullDetails.staking.data?.validator} format="staking" />
            <DisplaySection title="Rewards" data={fullDetails.rewards.data} format="rewards" />
            <DisplaySection title="Delegations" data={fullDetails.delegations.data} format="delegations" />
            <DisplaySection title="Unbonding Delegations" data={fullDetails.unbonding.data} format="unbonding" />
          </div>
        )}

        {allValidators.length > 0 && (
          <div className="validator-sections">
            <h2>All Validators</h2>
            <Link to="/Services?chain=terra&status=all">View all validators, staking, uptime and governance →</Link>
          </div>
        )}
      </div>
    </div>
  );
};

const DisplaySection = ({ title, data, format }) => {
  if (!data) return null;

  const formatDenom = (denom, amount) => denom === 'uluna' ? `${amount} LUNC` : `${amount} ${denom}`;

  return (
    <div className="validator-section">
      <h3>{title}</h3>
      {format === 'staking' && (
        <div>
          <p><strong>Operator:</strong> {data.operator_address}</p>
          <p><strong>Status:</strong> {data.status}</p>
          <p><strong>Tokens:</strong> {data.tokens} LUNC</p>
          <p><strong>Shares:</strong> {data.delegator_shares}</p>
          <p><strong>Commission:</strong> {(parseFloat(data.commission.commission_rates.rate) * 100).toFixed(2)}%</p>
          <p><strong>Moniker:</strong> {data.description.moniker}</p>
        </div>
      )}
      {format === 'rewards' && (
        <div>
          {data.total.map((item, i) => (
            <p key={i}>{formatDenom(item.denom, item.amount)}</p>
          ))}
        </div>
      )}
      {format === 'delegations' && (
        <div>
          {data.delegation_responses.map((item, i) => (
            <div key={i}>
              <p><strong>Delegator:</strong> {item.delegation.delegator_address}</p>
              <p><strong>Shares:</strong> {item.delegation.shares}</p>
              <p><strong>Balance:</strong> {formatDenom(item.balance.denom, item.balance.amount)}</p>
            </div>
          ))}
        </div>
      )}
      {format === 'unbonding' && (
        <p>No unbonding delegations.</p>
      )}
    </div>
  );
};

export default LoggedInPage;
